/**
 * Connection Status Dialog
 * 
 * Shows existing financial institution connections and provides options
 * to manage accounts across multiple connected institutions.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, ArrowLeft, RefreshCw, Plus, Building2 } from "lucide-react";
import Image from '@/components/ui/image';

// Lightweight display shape decoupled from DB hook
interface ConnectionDisplay {
  id: string;
  institutionId: string;
  institutionName: string;
  providerName: string;
  connectionStatus: string;
  logoUrl?: string | null;
}

interface ConnectionStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connections: ConnectionDisplay[];
  onViewInvestments: () => void;
  onUpdateAccounts: (connection: ConnectionDisplay) => void;
  onAddNewAccount: () => void;
}

export function ConnectionStatusDialog({
  open,
  onOpenChange,
  connections,
  onViewInvestments,
  onUpdateAccounts,
  onAddNewAccount
}: ConnectionStatusDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Connected
          </DialogTitle>
          <DialogDescription>
            You have {connections?.length || 0} connected financial institution{(connections?.length || 0) > 1 ? 's' : ''}. 
            What would you like to do?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3">
          {(connections || []).map((connection) => (
            <Card key={connection.id} className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {connection.logoUrl ? (
                      <div className="w-5 h-5 overflow-hidden flex-shrink-0 rounded-sm bg-white p-0.5 shadow-sm">
                        <Image 
                          src={connection.logoUrl} 
                          alt={connection.institutionName} 
                          className="h-full w-full object-contain" 
                          width={20}
                          height={20}
                          enableOptimization={false}
                        />
                      </div>
                    ) : (
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div>
                      <div className="text-sm font-medium">{connection.institutionName}</div>
                      <div className="text-xs text-muted-foreground">
                        Status: {connection.connectionStatus === 'connected' ? 'Active' : 'Needs attention'}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-3 pt-2">
          <Button 
            onClick={onViewInvestments}
            variant="outline"
            className="w-full justify-start gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            View Investments
          </Button>
          
          <Button 
            onClick={onAddNewAccount}
            className="w-full justify-start gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Institution
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export with old name for backward compatibility
export const WealthicaConnectionStatusDialog = ConnectionStatusDialog;
